import React, { useState, useEffect, useCallback } from 'react';
import { apiGetGroups, apiGetEmployeesByGroup } from '../../api';
import { apiGetRawSalaryData, apiConfirmSalary } from '../../api/index';
import { message, Select, InputNumber } from 'antd';
import { ClipLoader } from 'react-spinners';

const { Option } = Select;

// --- Helper Functions ---
const formatDisplayNumber = (value) => {
    if (value === null || value === undefined || isNaN(value)) return '0';
    if (value === 0) return '-';
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};
const formatInputNumber = (value) => {
    if (value === null || value === undefined || isNaN(value) || Number(value) === 0) return '';
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};
const inputParser = (value) => value.replace(/\./g, '');

// --- Standalone Calculation Functions (Updated for 2026 Rules) ---
const calculatePersonalIncomeTax = (taxableIncome, statutoryDeductions, dependents, yymmString) => {
    // yymmString expected format: "YYYYMM" or "YYYY-MM"
    let year, month;
    if (yymmString.includes('-')) {
        const parts = yymmString.split('-');
        year = parseInt(parts[0], 10);
        month = parseInt(parts[1], 10);
    } else {
        year = parseInt(yymmString.substring(0, 4), 10);
        month = parseInt(yymmString.substring(4, 6), 10);
    }

    // 1. Xác định mức giảm trừ gia cảnh (Áp dụng từ 01/2026)
    let personalRelief, dependentReliefUnit;
    if (year >= 2026) {
        personalRelief = 15500000;
        dependentReliefUnit = 6200000;
    } else {
        personalRelief = 11000000;
        dependentReliefUnit = 4400000;
    }

    const dependentRelief = dependentReliefUnit * (dependents || 0);
    const totalRelief = personalRelief + dependentRelief + statutoryDeductions;
    let assessableIncome = taxableIncome - totalRelief;

    if (assessableIncome <= 0) {
        return { personalIncomeTax: 0, assessableIncome: 0 };
    }

    let personalIncomeTax = 0;

    // 2. Xác định Biểu thuế lũy tiến
    const isNewTaxScheme = year >= 2026;

    if (isNewTaxScheme) {
        // Biểu thuế mới 5 bậc (Áp dụng từ 07/2026)
        if (assessableIncome <= 10000000) {
            personalIncomeTax = assessableIncome * 0.05;
        } else if (assessableIncome <= 30000000) {
            personalIncomeTax = 10000000 * 0.05 + (assessableIncome - 10000000) * 0.1;
        } else if (assessableIncome <= 60000000) {
            personalIncomeTax = 10000000 * 0.05 + 20000000 * 0.1 + (assessableIncome - 30000000) * 0.2;
        } else if (assessableIncome <= 100000000) {
            personalIncomeTax = 10000000 * 0.05 + 20000000 * 0.1 + 30000000 * 0.2 + (assessableIncome - 60000000) * 0.3;
        } else {
            personalIncomeTax = 10000000 * 0.05 + 20000000 * 0.1 + 30000000 * 0.2 + 40000000 * 0.3 + (assessableIncome - 100000000) * 0.35;
        }
    } else {
        // Biểu thuế cũ 7 bậc (Trước 07/2026)
        if (assessableIncome <= 5000000) {
            personalIncomeTax = assessableIncome * 0.05;
        } else if (assessableIncome <= 10000000) {
            personalIncomeTax = 250000 + (assessableIncome - 5000000) * 0.1;
        } else if (assessableIncome <= 18000000) {
            personalIncomeTax = 750000 + (assessableIncome - 10000000) * 0.15;
        } else if (assessableIncome <= 32000000) {
            personalIncomeTax = 1950000 + (assessableIncome - 18000000) * 0.2;
        } else if (assessableIncome <= 52000000) {
            personalIncomeTax = 4750000 + (assessableIncome - 32000000) * 0.25;
        } else if (assessableIncome <= 80000000) {
            personalIncomeTax = 9750000 + (assessableIncome - 52000000) * 0.3;
        } else {
            personalIncomeTax = 18150000 + (assessableIncome - 80000000) * 0.35;
        }
    }

    return { personalIncomeTax: Math.round(personalIncomeTax), assessableIncome };
};

const CalculateSalary = () => {
    // --- State Management ---
    const [groups, setGroups] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [calculatedData, setCalculatedData] = useState([]);
    const [insuranceSettings, setInsuranceSettings] = useState(null);
    
    const [isQuerying, setIsQuerying] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [loadingGroups, setLoadingGroups] = useState(false);
    const [loadingEmployees, setLoadingEmployees] = useState(false);

    const today = new Date();
    const defaultYymm = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;

    const [selectedMonth, setSelectedMonth] = useState(defaultYymm);
    const [selectedGroup, setSelectedGroup] = useState('ALL');
    const [selectedEmp, setSelectedEmp] = useState('ALL');

    // --- Recalculation Logic ---
    const runFullCalculation = useCallback((employeeData, currentInsuranceSettings, changedField, calculationMonth) => {
        if (!currentInsuranceSettings) return { ...employeeData, finalSalary: 0 };

        const enrichedEmp = { ...employeeData };

        // --- 1. Tính toán Ngày Công (Workdays) ---
        // Logic: Ngày công = (Min(Ngày công chuẩn, 26)) - (Tổng giờ nghỉ / 8)
        const kzhour = enrichedEmp.kzhour || 0;
        let finalWorkdays = enrichedEmp.workdays;

        // Chỉ tính lại cho nhân viên làm trọn tháng
        if (!enrichedEmp.isPartialMonthEmployee && enrichedEmp.standardMonthDays) {
            let baseDays = enrichedEmp.standardMonthDays;
            if (baseDays > 26) baseDays = 26; // Quy chuẩn tối đa 26 ngày trước khi trừ nghỉ
            
            const leaveDays = kzhour / 8.0;
            finalWorkdays = baseDays - leaveDays;
            
            // Giới hạn max 26 công và không âm
            if (finalWorkdays > 26) finalWorkdays = 26;
            if (finalWorkdays < 0) finalWorkdays = 0;
            
            finalWorkdays = parseFloat(finalWorkdays.toFixed(2));
        }
        enrichedEmp.workdays = finalWorkdays;

        // --- RULE: Zero Salary for Zero Workdays ---
        if (finalWorkdays <= 0) {
            const All_CB_Zero = (enrichedEmp.BB || 0) + (enrichedEmp.CV || 0) + (enrichedEmp.PHU || 0) + (enrichedEmp.NN || 0) + (enrichedEmp.KT || 0) + (enrichedEmp.MT || 0) + (enrichedEmp.TTKH || 0);
            return {
                ...enrichedEmp, workdays: 0,
                final_QC: 0, h1:0, h2:0, h3:0, b3:0, b4:0, b5:0, H1M: 0, H2M: 0, TT30: 0, 
                TT21: 0, TT05: 0, TT03: 0, JX: 0, TNKH: 0, totalIncome: 0, TotalLeaveDeduction: 0,
                personalIncomeTax: 0, finalSalary: 0,
                All_CB: All_CB_Zero, Hr_salary: 0,
            };
        }

        // --- Calculate Hourly Rate (Hr_salary) ---
        let insuranceBaseSalary = 0;
        if (currentInsuranceSettings && currentInsuranceSettings.calculationItems) {
            for (const key in currentInsuranceSettings.calculationItems) {
                if (currentInsuranceSettings.calculationItems[key] === true) {
                    insuranceBaseSalary += (enrichedEmp[key.trim()] || 0);
                }
            }
        }
        
        let Hr_salary = 0;
        // FIX: Tất cả nhân viên (kể cả nước ngoài) đều tính lương giờ theo công thức: Tổng lương / 26 / 8
        // Không phân biệt quốc tịch nữa
        Hr_salary = insuranceBaseSalary > 0 ? Math.round(insuranceBaseSalary / 26 / 8) : 0;
        
        enrichedEmp.Hr_salary = Hr_salary;

        // --- Calculate Other Initial Values ---
        const All_CB = (enrichedEmp.BB || 0) + (enrichedEmp.CV || 0) + (enrichedEmp.PHU || 0) + (enrichedEmp.NN || 0) + (enrichedEmp.KT || 0) + (enrichedEmp.MT || 0) + (enrichedEmp.TTKH || 0);

        // --- Attendance Bonus (QC) ---
        let final_QC = 0;
        
        // Priority 1: Người dùng sửa trực tiếp trên giao diện
        if (changedField === 'final_QC') {
            final_QC = Number(enrichedEmp.final_QC) || 0;
        } 
        // Priority 2: Đã có dữ liệu lưu trong DB (saved_QC từ backend) -> Giữ nguyên, không tính lại theo công thức
        else if (enrichedEmp.saved_QC !== undefined && enrichedEmp.saved_QC !== null) {
            final_QC = enrichedEmp.saved_QC;
        } 
        // Priority 3: Tính toán theo công thức
        else {
            const isPartialMonthEmployee = enrichedEmp.isPartialMonthEmployee;
            const sickLeaveHours = enrichedEmp.sickLeaveHours || 0;
            const personalLeaveHours = enrichedEmp.personalLeaveHours || 0;
            const totalHoursToDeductFromWorkdays = (enrichedEmp.totalDeductedHours || 0);

            const sickLeaveDays = sickLeaveHours / 8;
            const personalLeaveDays = personalLeaveHours / 8;
            const totalLeaveDaysForQC = totalHoursToDeductFromWorkdays / 8;
            
            const baseQC = enrichedEmp.QC || 0; 
            final_QC = baseQC;

            if (isPartialMonthEmployee) {
                final_QC = 0;
            } else if (totalLeaveDaysForQC >= 3) {
                final_QC = 0;
            } else if (sickLeaveDays > 2) {
                final_QC = 0;
            } else if (personalLeaveDays >= 2) { 
                final_QC = 0;
            } else if (sickLeaveDays >= 1 && personalLeaveDays >= 1) {
                final_QC = 0;
            } else if (sickLeaveDays > 1 && sickLeaveDays <= 2) {
                final_QC = baseQC * 0.5;
            } else if (personalLeaveDays > 0) { 
                final_QC = baseQC * 0.5;
            }
        }
        
        // --- Allowances and Overtime ---
        const H1M = Math.round((enrichedEmp.h1 || 0) * Hr_salary * 1.5);
        const H2M = Math.round((enrichedEmp.h2 || 0) * Hr_salary * 2.0);
        const TT30 = Math.round((enrichedEmp.h3 || 0) * Hr_salary * 3.0);
        const TT21 = Math.round((enrichedEmp.b3 || 0) * Hr_salary * 2.1);
        const TT05 = Math.round((enrichedEmp.b4 || 0) * Hr_salary * 1.5);
        const TT03 = Math.round((enrichedEmp.b5 || 0) * Hr_salary * 0.3);

        // --- Leave Deduction (UPDATED SIMPLE LOGIC) ---
        // Formula: Lương giờ * Số giờ nghỉ (backend kzhour)
        const TotalLeaveDeduction = Math.round(Hr_salary * kzhour);

        console.log(`[Frontend Calc] Emp: ${enrichedEmp.empid} | Workdays: ${finalWorkdays} | Hr_salary: ${formatDisplayNumber(Hr_salary)} | kzhour: ${kzhour} | Deduct: ${formatDisplayNumber(TotalLeaveDeduction)}`);

        // --- Gross Income ---
        const totalIncome = All_CB
                           + Math.round(final_QC)
                           + H1M + H2M + TT30 + TT21 + TT05 + TT03
                           + (enrichedEmp.TNKH || 0)
                           + (enrichedEmp.JX || 0)
                           + (enrichedEmp.tbtr || 0);
        
        // --- Deductions ---
        let insurance = { total_insurance: 0, union_fee: 0 };
        let tax = { personalIncomeTax: 0 };
        const qitaDeduction = enrichedEmp.QITA || 0;

        if (enrichedEmp.country === 'VN') {
            insurance = {
                total_insurance: enrichedEmp.total_insurance || 0,
                union_fee: enrichedEmp.union_fee || 0,
            };
            
            const incomeBaseForTax = totalIncome - TotalLeaveDeduction;
            const statutoryDeductions = insurance.total_insurance + insurance.union_fee + qitaDeduction;
            
            // FIX: Nếu STS == 'D' thì không tính người phụ thuộc (dependents = 0)
            let effectiveDependents = enrichedEmp.person_qty || 0;
            if (enrichedEmp.sts === 'D') {
                effectiveDependents = 0;
            }

            tax = calculatePersonalIncomeTax(incomeBaseForTax, statutoryDeductions, effectiveDependents, calculationMonth);
        }
        
        // --- Final Salary ---
        const finalSalary = totalIncome 
                          - TotalLeaveDeduction
                          - insurance.total_insurance
                          - insurance.union_fee
                          - qitaDeduction
                          - tax.personalIncomeTax;
        
        return {
            ...enrichedEmp, All_CB, Hr_salary, H1M, H2M, 
            workdays: finalWorkdays, 
            TotalLeaveDeduction,
            final_QC: Math.round(final_QC),
            TT30, TT21, TT05, TT03,
            total_insurance: insurance.total_insurance,
            union_fee: insurance.union_fee,
            totalIncome: totalIncome,
            personalIncomeTax: tax.personalIncomeTax, 
            finalSalary: Math.round(finalSalary),
        };
    }, []);

    // --- Data Fetching & Handlers ---
    useEffect(() => {
        const fetchGroups = async () => {
            setLoadingGroups(true);
            try {
                const groupsData = await apiGetGroups();
                setGroups(groupsData);
            } catch (error) {
                message.error('Không thể tải danh sách bộ phận.');
            } finally {
                setLoadingGroups(false);
            }
        };
        fetchGroups();
    }, []);
    
    const handleGroupChange = useCallback(async (groupid) => {
        setSelectedGroup(groupid);
        setSelectedEmp('ALL');
        setCalculatedData([]);
    
        if (!selectedMonth) {
            message.warning('Vui lòng chọn tháng trước.');
            setEmployees([]);
            return;
        }
    
        const yymm = selectedMonth.replace('-', '');
        setLoadingEmployees(true);
        try {
            const data = await apiGetEmployeesByGroup(groupid, yymm);
            setEmployees(data);
        } catch (error) {
            message.error('Không thể tải danh sách nhân viên.');
        } finally {
            setLoadingEmployees(false);
        }
    }, [selectedMonth]);

    useEffect(() => {
        handleGroupChange(selectedGroup);
    }, [selectedMonth, selectedGroup, handleGroupChange]);
    
    const handleQuery = async () => {
        if (!selectedMonth) {
            message.warn('Vui lòng chọn tháng tính lương.');
            return;
        }
        setIsQuerying(true);
        setCalculatedData([]);
        try {
            const payload = { yymm: selectedMonth.replace('-', ''), groupid: selectedGroup, empid: selectedEmp };
            const result = await apiGetRawSalaryData(payload);
            
            if (result.rawData && result.rawData.length > 0) {
                const newInsuranceSettings = result.insuranceSettings;
                setInsuranceSettings(newInsuranceSettings);
                
                // FIX: Không tự động tính toán lại khi load. Chỉ hiển thị dữ liệu gốc từ Backend.
                // Việc tính toán lại chỉ xảy ra trong handleValueChange
                setCalculatedData(result.rawData);
                message.success(`Đã tải dữ liệu cho ${result.rawData.length} nhân viên.`);
            } else {
                message.info('Không có dữ liệu để tính lương cho lựa chọn này.');
            }
        } catch (error) {
            message.error(error.message || 'Có lỗi xảy ra khi tra cứu.');
        } finally {
            setIsQuerying(false);
        }
    };

    const handleValueChange = (empid, field, value) => {
        setCalculatedData(prevData =>
            prevData.map(item => {
                if (item.empid === empid) {
                    const updatedItem = { ...item, [field]: value };
                    // Khi sửa, gọi hàm tính toán lại
                    return runFullCalculation(updatedItem, insuranceSettings, field, selectedMonth);
                }
                return item;
            })
        );
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const payload = { yymm: selectedMonth.replace('-', ''), salaryData: calculatedData };
            const result = await apiConfirmSalary(payload);
            message.success(result.message || 'Lưu bảng lương thành công!');
            setCalculatedData([]);
        } catch (error) {
            message.error(error.message || 'Có lỗi xảy ra khi lưu.');
        } finally {
            setIsSaving(false);
        }
    };

    const headerGroups = {
        info: ['MSNV', 'Họ Tên', 'Chức Vụ', 'Đơn vị', 'Ngày công', 'Lương giờ'],
        earnings: ['Lương CB', 'Chức Vụ', 'Điện Thoại', 'Xăng Xe', 'Kỹ Thuật', 'Môi Trường', 'Nhà Ở', 'Chuyên Cần'],
        overtimeAndBonus: [
            'TC 1.5', 'TT 1.5', 'TC 2.0', 'TT 2.0', 'TC 3.0', 'TT 3.0',
            'TC 2.1', 'TT 2.1', 'TC 0.5', 'TT 0.5', 'TC 0.3', 'TT 0.3',
            'Thưởng Hiệu quả', 'Thu nhập khác'
        ],
        summary: ['TỔNG THU NHẬP'],
        deductions: ['Trừ phép/vắng', 'BHXH', 'BHYT', 'BHTN', 'Tổng BHXH', 'Phí công đoàn', 'Trừ khác', 'Thuế TNCN'],
        final: ['THỰC LÃNH']
    };
    
    const renderCell = (row, key, options = {}) => {
        const { isEditable = false, isHighlight = false } = options;
        if (isEditable) {
            return (
                <InputNumber 
                    value={row[key]} 
                    onChange={v => handleValueChange(row.empid, key, v)} 
                    bordered={false} 
                    style={{ width: '100%', fontFamily: 'inherit', backgroundColor: 'transparent' }} 
                    className="text-right px-2" 
                    formatter={formatInputNumber} 
                    parser={inputParser} 
                    controls={false}
                />
            );
        }
        return <div className={`p-2 text-right ${isHighlight ? 'font-bold' : ''}`}>{formatDisplayNumber(row[key])}</div>;
    };

    return (
        <div style={{ fontFamily: '"Times New Roman", Times, serif' }}>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Tính Lương & Chốt Sổ (Tương tác)</h2>
            
            <div className="bg-gray-50 p-4 rounded-lg flex flex-wrap items-end gap-4 mb-6">
                 <div>
                    <label className="block text-sm font-medium text-gray-700">Tháng</label>
                    <input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className="mt-1 p-2 border rounded-md" style={{ fontFamily: 'inherit' }} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Bộ phận</label>
                    <Select value={selectedGroup} onChange={handleGroupChange} loading={loadingGroups} style={{ width: 240 }} className="mt-1">
                        <Option value="ALL">-- Tất cả --</Option>
                        {groups.map(g => <Option key={g.groupId} value={g.groupId}>{g.groupName} ({g.groupId})</Option>)}
                    </Select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Nhân viên</label>
                    <Select
                        showSearch
                        value={selectedEmp}
                        onChange={setSelectedEmp}
                        loading={loadingEmployees}
                        disabled={loadingEmployees || !employees.length}
                        style={{ width: 240 }}
                        placeholder="Tìm và chọn nhân viên"
                        optionFilterProp="children"
                        filterOption={(input, option) => {
                            const optionText = (Array.isArray(option.children) ? option.children.join('') : option.children) || '';
                            return optionText.toLowerCase().includes(input.toLowerCase());
                        }}
                        className="mt-1"
                    >
                        <Option value="ALL">-- Tất cả --</Option>
                        {employees.map(e => <Option key={e.empid} value={e.empid}>{e.name} ({e.empid})</Option>)}
                    </Select>
                </div>
                 <div className="flex items-center gap-4">
                    <button onClick={handleQuery} disabled={isQuerying || isSaving} className="px-6 py-2 font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-400">
                        {isQuerying ? <><ClipLoader size={20} color={"#ffffff"} className="mr-2" /> Tải...</> : 'Tải & Tính Lương'}
                    </button>
                    <button onClick={handleSave} disabled={isSaving || isSaving || calculatedData.length === 0} className="px-6 py-2 font-semibold text-white bg-green-600 rounded-md hover:bg-green-700 disabled:bg-gray-400">
                        {isSaving ? <><ClipLoader size={20} color={"#ffffff"} className="mr-2" /> Lưu...</> : 'Xác nhận & Chốt Sổ'}
                    </button>
                </div>
            </div>

            {(isQuerying || calculatedData.length > 0) && (
                 <div className="mt-6">
                    <div className="overflow-auto bg-white rounded-lg shadow border" style={{ maxHeight: '75vh' }}>
                        <table className="min-w-full text-sm">
                           <thead className="sticky top-0 bg-gray-100 z-20">
                                <tr>
                                    {Object.keys(headerGroups).map(groupKey => (
                                        <th key={groupKey} colSpan={headerGroups[groupKey].length} className="p-2 border-b border-r text-center font-semibold text-gray-700">{groupKey.replace(/([A-Z])/g, ' $1').trim().toUpperCase()}</th>
                                    ))}
                                </tr>
                                <tr>
                                    {headerGroups.info.map((label, index) => {
                                        const widths = ['90px', '180px', '110px', '110px', '90px', '100px'];
                                        const lefts = ['0px', '90px'];
                                        let thClassName = "p-2 border-b border-r font-semibold text-gray-600 whitespace-nowrap";
                                        let thStyle = { width: widths[index], minWidth: widths[index] };

                                        if (index < 2) {
                                            thClassName += " sticky bg-gray-100 z-30";
                                            thStyle.left = lefts[index];
                                        }
                                        return <th key={`info-${index}`} className={thClassName} style={thStyle}>{label}</th>;
                                    })}
                                    {Object.values(headerGroups).slice(1).flat().map((label, index) => (
                                        <th key={`other-${index}`} className="p-2 border-b border-r font-semibold text-gray-600 whitespace-nowrap" style={{minWidth: '100px'}}>{label}</th>
                                    ))}
                                </tr>
                            </thead>
                             <tbody className="divide-y divide-gray-200">
                                {isQuerying ? (
                                    <tr><td colSpan={Object.values(headerGroups).flat().length} className="p-4 text-center">Đang tính toán dữ liệu...</td></tr>
                                ) : calculatedData.map(row => (
                                    <tr key={row.empid} className="hover:bg-gray-50">
                                        {/* INFO */}
                                        <td className="p-2 border-r sticky z-10 bg-white hover:bg-gray-50 font-medium text-blue-600" style={{left: '0px', width: '90px', minWidth: '90px'}}>{row.empid}</td>
                                        <td className="p-2 border-r sticky z-10 bg-white hover:bg-gray-50" style={{left: '90px', width: '180px', minWidth: '180px'}}>{row.empnam_vn}</td>
                                        <td className="p-2 border-r">{row.lj}</td>
                                        <td className="p-2 border-r">{row.lg}</td>
                                        <td className="p-2 border-r text-right">{row.workdays}</td>
                                        <td className="p-2 border-r text-right">{formatDisplayNumber(row.Hr_salary)}</td>
                                        
                                        {/* EARNINGS - Green color */}
                                        <td className="p-0 whitespace-nowrap border-r bg-green-50">{renderCell(row, 'BB', {isEditable: true})}</td>
                                        <td className="p-0 whitespace-nowrap border-r bg-green-50">{renderCell(row, 'CV', {isEditable: true})}</td>
                                        <td className="p-0 whitespace-nowrap border-r bg-green-50">{renderCell(row, 'PHU', {isEditable: true})}</td>
                                        <td className="p-0 whitespace-nowrap border-r bg-green-50">{renderCell(row, 'NN', {isEditable: true})}</td>
                                        <td className="p-0 whitespace-nowrap border-r bg-green-50">{renderCell(row, 'KT', {isEditable: true})}</td>
                                        <td className="p-0 whitespace-nowrap border-r bg-green-50">{renderCell(row, 'MT', {isEditable: true})}</td>
                                        <td className="p-0 whitespace-nowrap border-r bg-green-50">{renderCell(row, 'TTKH', {isEditable: true})}</td>
                                        <td className="p-0 whitespace-nowrap border-r bg-green-50">{renderCell(row, 'final_QC', {isEditable: true})}</td>

                                        {/* OVERTIME & BONUS - Green color */}
                                        <td className="p-0 whitespace-nowrap border-r bg-green-50">{renderCell(row, 'h1')}</td>
                                        <td className="p-0 whitespace-nowrap border-r bg-green-50">{renderCell(row, 'H1M')}</td>
                                        <td className="p-0 whitespace-nowrap border-r bg-green-50">{renderCell(row, 'h2')}</td>
                                        <td className="p-0 whitespace-nowrap border-r bg-green-50">{renderCell(row, 'H2M')}</td>
                                        <td className="p-0 whitespace-nowrap border-r bg-green-50">{renderCell(row, 'h3')}</td>
                                        <td className="p-0 whitespace-nowrap border-r bg-green-50">{renderCell(row, 'TT30')}</td>
                                        <td className="p-0 whitespace-nowrap border-r bg-green-50">{renderCell(row, 'b3')}</td>
                                        <td className="p-0 whitespace-nowrap border-r bg-green-50">{renderCell(row, 'TT21')}</td>
                                        <td className="p-0 whitespace-nowrap border-r bg-green-50">{renderCell(row, 'b4')}</td>
                                        <td className="p-0 whitespace-nowrap border-r bg-green-50">{renderCell(row, 'TT05')}</td>
                                        <td className="p-0 whitespace-nowrap border-r bg-green-50">{renderCell(row, 'b5')}</td>
                                        <td className="p-0 whitespace-nowrap border-r bg-green-50">{renderCell(row, 'TT03')}</td>
                                        <td className="p-0 whitespace-nowrap border-r bg-green-50">{renderCell(row, 'JX', {isEditable: true})}</td>
                                        <td className="p-0 whitespace-nowrap border-r bg-green-50">{renderCell(row, 'TNKH', {isEditable: true})}</td>
                                        
                                        {/* SUMMARY - Blue color */}
                                        <td className="p-0 whitespace-nowrap border-r bg-blue-50 font-bold">{renderCell(row, 'totalIncome', {isHighlight: true})}</td>

                                        {/* DEDUCTIONS - Red color */}
                                        <td className="p-0 whitespace-nowrap border-r bg-red-50">{renderCell(row, 'TotalLeaveDeduction')}</td>
                                        <td className="p-0 whitespace-nowrap border-r bg-red-50">{renderCell(row, 'bhxh')}</td>
                                        <td className="p-0 whitespace-nowrap border-r bg-red-50">{renderCell(row, 'bhyt')}</td>
                                        <td className="p-0 whitespace-nowrap border-r bg-red-50">{renderCell(row, 'bhtn')}</td>
                                        <td className="p-0 whitespace-nowrap border-r bg-red-50 font-bold">{renderCell(row, 'total_insurance')}</td>
                                        <td className="p-0 whitespace-nowrap border-r bg-red-50">{renderCell(row, 'union_fee')}</td>
                                        <td className="p-0 whitespace-nowrap border-r bg-red-50">{renderCell(row, 'QITA', {isEditable: true})}</td>
                                        <td className="p-0 whitespace-nowrap border-r bg-red-50">{renderCell(row, 'personalIncomeTax')}</td>

                                        {/* FINAL - Blue color */}
                                        <td className="p-0 whitespace-nowrap border-r bg-blue-100 font-bold">{renderCell(row, 'finalSalary', {isHighlight: true})}</td>
                                    </tr>
                                )
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CalculateSalary;